"use client"

import { ArrowUpRight, Check, Eye } from "lucide-react"

import { cn } from "@/common/lib/utils"
import RadarChart from "@/modules/lernpfad/components/charts/radar-chart"
import {
    FRAMEWORK_AXES,
    frameworkProfile,
    type FrameworkId,
    type LearningFramework,
} from "@/modules/lernpfad/data/frameworks"
import type { Localizer } from "@/modules/lernpfad/i18n/localize"
import { CLUSTER_ORDER, overridesByCluster } from "./cluster-map"

/**
 * The prose half of the framework panel — rendered ONCE, for whichever framework
 * is selected or hovered.
 *
 * Everything here is incomparable across frameworks: the argument it makes, the
 * list of parameter effects, the source. Six copies of it was the panel's whole
 * length problem. One copy that follows the pointer costs nothing per option.
 */

export interface FrameworkDossierProps {
    /** The framework under the pointer / keyboard focus, else the active one. */
    subject: LearningFramework | undefined
    localizer: Localizer
    selected: FrameworkId[]
    frameworks: LearningFramework[]
    /** True when `subject` is merely being previewed, not applied. */
    previewOnly: boolean
    /** Jump to a parameter cluster the framework wrote. */
    onOpenCluster: (cluster: string) => void
    t: (key: never, values?: Record<string, string | number>) => string
}

export default function FrameworkDossier({
    subject,
    localizer,
    selected,
    frameworks,
    previewOnly,
    onOpenCluster,
    t,
}: FrameworkDossierProps) {
    const combined = frameworkProfile(selected)
    const anySelected = selected.length > 0

    // The house standard is a real state, not an absence, so it gets the same
    // box rather than leaving an empty pentagon as the first thing you see.
    if (!subject) {
        return (
            <section className="lp-card rounded-xl border bg-card">
                <div className="border-b px-4 py-2.5">
                    <h3 className="text-sm font-semibold">{t("steuer.standard" as never)}</h3>
                </div>
                <div className="space-y-3 p-4">
                    <p className="text-sm leading-relaxed text-muted-foreground">{t("steuer.none" as never)}</p>
                    <p className="text-xs text-muted-foreground">{t("steuer.writes.other" as never, { count: 0 })}</p>
                </div>
            </section>
        )
    }

    const content = localizer.framework(subject)
    const active = selected.includes(subject.id)
    const touched = overridesByCluster(subject.patch)
    const clusters = CLUSTER_ORDER.filter((id) => touched[id] > 0)
    const writes = clusters.reduce((sum, id) => sum + touched[id], 0)

    return (
        <section className="lp-card rounded-xl border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
                <h3 className="text-sm font-semibold">{content.name}</h3>
                {active ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        <Check className="size-3" />
                        {t("steuer.active" as never)}
                    </span>
                ) : (
                    previewOnly && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                            <Eye className="size-3" />
                            {t("steuer.preview" as never)}
                        </span>
                    )
                )}
            </div>

            <div className="space-y-4 p-4">
                <div className="min-w-0 space-y-3">
                    {/* The pane runs to ~836px of usable width, which is far past
                        a comfortable measure, so the prose is capped rather than
                        stretched. */}
                    <p className="max-w-[68ch] text-sm leading-relaxed">{content.claim}</p>

                    <div className="max-w-[68ch] rounded-lg bg-muted/50 p-3">
                        <p className="mb-2 text-xs font-medium text-muted-foreground">{t("steuer.changes" as never)}</p>
                        <ul className="space-y-1.5">
                            {content.changes.map((change) => (
                                <li key={change} className="flex gap-2 text-xs leading-relaxed">
                                    <span aria-hidden className="mt-[6px] size-1 shrink-0 rounded-full bg-primary/60" />
                                    <span>{change}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* What it wrote, and where. Each pill jumps to that cluster
                        on the Parameter tab, closing the loop with the reverse
                        "von {framework} gesetzt" link already rendered there. */}
                    {clusters.length > 0 && (
                        <div>
                            <p className="mb-1.5 text-xs text-muted-foreground">
                                {t((writes === 1 ? "steuer.wrote.one" : "steuer.wrote.other") as never, {
                                    count: writes,
                                })}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {clusters.map((id) => (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => onOpenCluster(id)}
                                        className={cn(
                                            "rounded-md border px-2 py-0.5 text-xs font-medium transition-colors",
                                            "hover:border-primary/50 hover:bg-primary/5 hover:text-primary",
                                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                        )}
                                    >
                                        {t(`steuer.cluster.${id}` as never)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <a
                        href={subject.source}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:underline"
                    >
                        {t("steuer.source" as never)}
                        <ArrowUpRight className="size-3" />
                    </a>
                </div>

                {/* Below the explanation, not beside it. In a 220px side column
                    the axis labels rendered at ~6.5px and the shape — the one
                    thing a radar is actually good at — was the smallest element
                    in the pane. */}
                <div className="min-w-0 border-t pt-4">
                    <p className="mb-2 text-xs font-medium text-muted-foreground">{t("steuer.radar" as never)}</p>
                    <div className="flex justify-center">
                        <RadarChart
                            size={420}
                            axes={FRAMEWORK_AXES.map((axis) => ({
                                id: axis,
                                label: t(`steuer.axis.${axis}` as never),
                                value: anySelected ? combined[axis] : subject.profile[axis],
                            }))}
                            // Dashed member outlines, so a second framework whose
                            // profile is subsumed by the first still reads as
                            // present. Without them `Math.max` makes the shape
                            // identical and the chart says "nothing changed".
                            series={
                                anySelected
                                    ? selected.map((id) => {
                                          const entry = frameworks.find((f) => f.id === id)
                                          return {
                                              id,
                                              values: FRAMEWORK_AXES.map((axis) => entry?.profile[axis] ?? 0),
                                          }
                                      })
                                    : undefined
                            }
                        />
                    </div>
                    <p className="mt-1 text-center text-xs text-muted-foreground">{t("steuer.radarRule" as never)}</p>
                </div>
            </div>
        </section>
    )
}
